import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
  IonFab,
  IonFabButton,
  useIonToast,
  useIonViewWillEnter,
} from '@ionic/react';
import { addOutline, attachOutline, briefcaseOutline, chevronDownCircleOutline, closeOutline, cubeOutline, filterOutline, peopleOutline } from 'ionicons/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import { AppContext } from '../../providers/AppContext';

const PAGE_SIZE = 20;

// ─── Shared style tokens ──────────────────────────────────────────────────────
const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#dde7f5',
  color: '#1d508d',
  borderRadius: '16px',
  padding: '3px 10px',
  fontSize: '12px',
  marginRight: '6px',
  marginBottom: '4px',
};

const chipRemoveStyle: React.CSSProperties = {
  cursor: 'pointer',
  marginLeft: '6px',
  fontWeight: 'bold',
  fontSize: '14px',
  lineHeight: 1,
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: '160px',
  overflowY: 'auto',
  backgroundColor: 'white',
  border: '1px solid #ccc',
  zIndex: 99,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  borderRadius: '0 0 8px 8px',
};

const dropdownItemStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid #f0f0f0',
  color: '#222',
  cursor: 'pointer',
  fontSize: '13px',
};

const pillGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '6px',
};

const pillStyle = (active: boolean): React.CSSProperties => ({
  padding: '6px 16px',
  borderRadius: '20px',
  fontSize: '13px',
  cursor: 'pointer',
  border: `1.5px solid ${active ? '#1d508d' : '#ccc'}`,
  background: active ? '#1d508d' : 'white',
  color: active ? 'white' : '#555',
  transition: 'all 0.15s ease',
  userSelect: 'none',
});

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 700,
  color: '#6b7b8f',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginTop: '20px',
  marginBottom: '6px',
};

// ─── Component ────────────────────────────────────────────────────────────────
const DocumentsHome: React.FC = () => {
  const { t } = useTranslation();
  const isMounted = useRef(true);
  const [present] = useIonToast();
  const { state } = useContext(AppContext);

  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  // ── Sort ──────────────────────────────────────────────────────
  const [sortField, setSortField] = useState<'expiry_date' | 'creation' | ''>('expiry_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Existing filters ──────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // ── Top Metric Cards ──────────────────────────────────────────────────────
  const [complianceCards, setComplianceCards] = useState({
    project_compliance_documents: 0,
    asset_compliance_documents: 0,
    user_compliance_documents: 0
  });


  // ── Approach 1: entity value filters ─────────────────────────────────────
  const [filterProject, setFilterProject] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);

  const [filterAssets, setFilterAssets] = useState<string[]>([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [loadingAsset, setLoadingAsset] = useState(false);

  const [filterUsers, setFilterUsers] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  // ── Approach 2: existence toggles ─────────────────────────────────────────
  const [hasProject, setHasProject] = useState(false);
  const [hasAssets, setHasAssets] = useState(false);
  const [hasUsers, setHasUsers] = useState(false);

  // Badge count = number of active filters beyond "All" / empty defaults
  const activeFilterCount = [
    statusFilter !== 'All',
    !!filterProject,
    filterAssets.length > 0,
    filterUsers.length > 0,
    hasProject,
    hasAssets,
    hasUsers,
  ].filter(Boolean).length;

  // ── Permission ────────────────────────────────────────────────────────────
  const canCreateDocuments =
    state.permissions?.documents_create !== undefined
      ? Boolean(state.permissions.documents_create)
      : state.permissions?.document_create !== undefined
        ? Boolean(state.permissions.document_create)
        : true;

  const getStatusColor = (status: string) => {
    const n = (status || '').toLowerCase();
    if (n === 'compliant' || n === 'completed') return '#00a65a';
    if (n.includes('non compliant') || n.includes('need renewal') || n.includes('need inspection'))
      return '#f56954';
    return '#f39c12';
  };

  // Returns { label, bg, color } for expiry chips, or null if no expiry date
  const getExpiryChip = (expiryDate: string | null | undefined): { label: string; bg: string; color: string } | null => {
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: t('Documents.Document_Expired'), bg: '#fdecea', color: '#c0392b' };
    if (daysLeft <= 30) return { label: t('Documents.Due_Renewal_in_30_Days'), bg: '#fff3e0', color: '#e65100' };
    if (daysLeft <= 90) return { label: t('Documents.About_to_Expire_in_90_Days'), bg: '#fffde7', color: '#f57f17' };
    return null;
  };

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchProjects = async (search: string) => {
    setLoadingProject(true);
    try {
      const res = await axios({
        method: 'get',
        url: `/resource/Project?filters=[["project_name","like","%${search}%"]]&limit_page_length=20`,
        headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
      });
      setProjectOptions(res?.data?.data || []);
    } catch (e) { console.log(e); }
    finally { setLoadingProject(false); }
  };

  const fetchAssets = async (search: string) => {
    setLoadingAsset(true);
    try {
      const res = await axios({
        method: 'get',
        url: `/resource/Asset?filters=[["name","like","%${search}%"]]&limit_page_length=20`,
        headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
      });
      setAssetOptions(res?.data?.data || []);
    } catch (e) { console.log(e); }
    finally { setLoadingAsset(false); }
  };

  const fetchUsers = async (search: string) => {
    setLoadingUser(true);
    try {
      const res = await axios({
        method: 'get',
        url: `/resource/User?filters=[["enabled","=",1],["name","like","%${search}%"]]&limit_page_length=20`,
        headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
      });
      setUserOptions(res?.data?.data || []);
    } catch (e) { console.log(e); }
    finally { setLoadingUser(false); }
  };

  // ── Selection handlers ────────────────────────────────────────────────────
  const selectProject = (p: any) => {
    const name = p.project_name || p.name;
    setFilterProject(name);
    setProjectSearch(name);
    setShowProjectDropdown(false);
  };
  const clearProject = () => { setFilterProject(''); setProjectSearch(''); };

  const selectAsset = (a: any) => {
    setFilterAssets((prev) => (prev.includes(a.name) ? prev : [...prev, a.name]));
    setAssetSearch('');
    setShowAssetDropdown(false);
  };
  const removeAsset = (name: string) => setFilterAssets((prev) => prev.filter((x) => x !== name));

  const selectUser = (u: any) => {
    setFilterUsers((prev) => (prev.includes(u.name) ? prev : [...prev, u.name]));
    setUserSearch('');
    setShowUserDropdown(false);
  };
  const removeUser = (name: string) => setFilterUsers((prev) => prev.filter((x) => x !== name));

  // ── Build base Frappe filter array (no existence toggles) ────────────────
  const buildBaseFilters = (
    nameQuery: string, status: string,
    project: string, assets: string[], users: string[],
  ) => {
    const filters: any[] = [];
    const userEmail = localStorage.getItem('email');
    if (nameQuery.trim()) filters.push(['name', 'like', `%${nameQuery.trim()}%`]);
    // if (userEmail) filters.push(['Compliance User Assignment', 'user', '=', userEmail]);
    if (status && status !== 'All') filters.push(['status', '=', status]);
    // Approach 1 — entity value filters
    if (project) filters.push(['Compliance Project Assignment', 'project', '=', project]);
    if (assets.length > 0) filters.push(['Compliance Asset Assignment', 'asset', 'in', assets]);
    if (users.length > 0) filters.push(['Compliance User Assignment', 'user', 'in', users]);
    return filters;
  };

  // ── Fetch all names matching a single child-table existence filter ─────────
  const fetchNamesByExistence = async (childFilter: any[]): Promise<Set<string>> => {
    try {
      const encoded = encodeURIComponent(JSON.stringify([childFilter]));
      const nameField = encodeURIComponent(JSON.stringify(['name']));
      const url = `/resource/Compliance%20Documents?fields=${nameField}&filters=${encoded}&limit_page_length=500&distinct=1`;
      const res = await axios({ method: 'get', url, headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` } });
      return new Set<string>((res?.data?.data || []).map((d: any) => d.name as string));
    } catch {
      return new Set<string>();
    }
  };

  // ── Load documents ─────────────────────────────────────────────────────────
  const loadDocuments = async (options: {
    page: number; append: boolean;
    search?: string; status?: string;
    project?: string; assets?: string[]; users?: string[];
    hp?: boolean; ha?: boolean; hu?: boolean;
  }) => {
    const {
      page, append,
      search = searchText, status = statusFilter,
      project = filterProject, assets = filterAssets, users = filterUsers,
      hp = hasProject, ha = hasAssets, hu = hasUsers,
    } = options;

    if (isMounted.current) setIsLoading(true);

    try {
      // Build the base filters (without existence toggles)
      const baseFilters = buildBaseFilters(search, status, project, assets, users);

      // Resolve existence toggles — each gets its own query, then we intersect
      const existenceChecks: any[][] = [];
      if (hp) existenceChecks.push(['Compliance Project Assignment', 'project', 'is', 'set']);
      if (ha) existenceChecks.push(['Compliance Asset Assignment', 'asset', 'is', 'set']);
      if (hu) existenceChecks.push(['Compliance User Assignment', 'user', 'is', 'set']);

      let intersectedNames: string[] | null = null;

      if (existenceChecks.length > 0) {
        // Fire all existence queries in parallel
        const nameSets = await Promise.all(existenceChecks.map(fetchNamesByExistence));

        // Intersect: start with the first set, keep only names present in ALL sets
        let intersection = nameSets[0];
        for (let i = 1; i < nameSets.length; i++) {
          intersection = new Set(Array.from(intersection).filter((n) => nameSets[i].has(n)));
        }
        intersectedNames = Array.from(intersection);

        // If intersection is empty there are no matching docs — bail early
        if (intersectedNames.length === 0) {
          if (isMounted.current) {
            setDocuments(append ? (prev) => prev : []);
            setDisableInfiniteScroll(true);
          }
          return;
        }
      }

      // Build final filter array: base filters + name-in intersection if needed
      const finalFilters = [...baseFilters];
      if (intersectedNames !== null) {
        finalFilters.push(['name', 'in', intersectedNames]);
      }

      // Fire count and document list in parallel
      const fields = encodeURIComponent(JSON.stringify(['status', 'name', 'document_number', 'document_type', 'issued_date', 'expiry_date', 'attachment']));
      const encodedFilters = encodeURIComponent(JSON.stringify(finalFilters));
      const getOrderBy = () => {
        if (!sortField) return '&order_by=%60tabCompliance%20Documents%60.creation%20desc';
        if (sortField === 'creation') return `&order_by=%60tabCompliance%20Documents%60.creation%20${sortDir}`;
        return `&order_by=${sortField}%20${sortDir}`;
      };
      const orderBy = getOrderBy();
      const url = `/resource/Compliance%20Documents?fields=${fields}&filters=${encodedFilters}&limit_page_length=${PAGE_SIZE}&limit_start=${page * PAGE_SIZE}&distinct=1${orderBy}`;

      const [response] = await Promise.all([
        axios({ method: 'get', url, headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` } }),
        page === 0 ? fetchCount(finalFilters) : Promise.resolve(),
      ]);

      const nextBatch = response?.data?.data || [];
      if (!isMounted.current) return;
      setDocuments(append ? (prev) => [...prev, ...nextBatch] : nextBatch);
      setCurrentPage(page + 1);
      setDisableInfiniteScroll(nextBatch.length < PAGE_SIZE);
    } catch (error: any) {
      console.log(error);
      if (isMounted.current && !append) setDocuments([]);
      present({ message: t('Utils.fetchF'), color: 'danger', duration: 2000 });
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };

  // ── Fetch total count (mirrors the same filters as loadDocuments) ──────────
  const fetchCount = async (
    finalFilters: any[],
  ) => {
    try {
      const encodedFilters = encodeURIComponent(JSON.stringify(finalFilters));
      const url = `/method/preflet_asset.api.get_documents_count?doc_name=Compliance%20Documents&filters=${encodedFilters}`;
      const res = await axios({ method: 'get', url, headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` } });
      if (isMounted.current) {
        setTotalCount(res?.data?.message?.total_count ?? null);
      }
    } catch (e) {
      console.log('Count fetch error', e);
    }
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
    loadDocuments({ page: 0, append: false });
  };

  const handleClearFilter = () => {
    setStatusFilter('All');
    setFilterProject(''); setProjectSearch('');
    setFilterAssets([]); setAssetSearch('');
    setFilterUsers([]); setUserSearch('');
    setHasProject(false); setHasAssets(false); setHasUsers(false);
    setSortField('expiry_date'); setSortDir('asc');
    setShowFilterModal(false);
    loadDocuments({
      page: 0, append: false,
      search: searchText, status: 'All',
      project: '', assets: [], users: [], hp: false, ha: false, hu: false,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    loadDocuments({ page: 0, append: false, search: value });
  };

  async function loadNextPage(event: CustomEvent<void>) {
    await loadDocuments({ page: currentPage, append: true });
    (event.target as HTMLIonInfiniteScrollElement).complete();
  }

  async function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    await loadDocuments({ page: 0, append: false });
    event.detail.complete();
  }

  // ── Fetch Compliance Metric Cards ─────────────────────────────────────────
  const fetchComplianceCards = async () => {
    try {
      const url = '/method/preflet_asset.api.compliance_documents_cards';
      const res = await axios({ method: 'get', url, headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` } });
      if (isMounted.current && res?.data?.data) {
        setComplianceCards({
          project_compliance_documents: res.data.data.project_compliance_documents || 0,
          asset_compliance_documents: res.data.data.asset_compliance_documents || 0,
          user_compliance_documents: res.data.data.user_compliance_documents || 0
        });
      }
    } catch (e) {
      console.log('Error fetching metrics', e);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    loadDocuments({ page: 0, append: false });
    fetchComplianceCards();
    return () => { isMounted.current = false; };
  }, []);

  // Re-fetch every time the page comes into view (e.g. navigating back from CreateDocument)
  useIonViewWillEnter(() => {
    loadDocuments({ page: 0, append: false });
    fetchComplianceCards();
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <IonPage>
      <Header enableBackButton="" title="" />
      <IonContent>

        {/* Pull-to-refresh */}
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          />
        </IonRefresher>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)',
          padding: '16px 16px 40px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', position: 'relative', zIndex: 1 }}>
              {t('Documents.list')}
            </p>
            {/* <h1 style={{ margin: '3px 0 0', fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.15 }}>Documents</h1> */}
            {totalCount !== null && (
              <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '3px 12px', border: '1px solid rgba(255,255,255,0.2)' }}>
                {totalCount} {t('Documents.total')}
              </span>
            )}
          </div>
        </div>

        {/* ── Metric Cards (floating out of banner) ── */}
        <div style={{ display: 'flex', gap: 10, padding: '0 12px', marginTop: -24, position: 'relative', zIndex: 2 }}>
          <div style={{ flex: 1, background: '#fff', borderRadius: 16, padding: '14px 10px', textAlign: 'center', boxShadow: '0 4px 20px rgba(29,80,141,0.13)', border: '1.5px solid rgba(29,80,141,0.08)' }}>
            <div style={{ background: '#e8f0fc', borderRadius: 10, width: 34, height: 34, margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IonIcon icon={briefcaseOutline} style={{ color: '#1d508d', fontSize: 18 }} />
            </div>
            <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: '#1d508d' }}>{complianceCards.project_compliance_documents}</h2>
            <p style={{ margin: '3px 0 0', fontSize: 9, fontWeight: 700, color: '#6b7b8f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('Documents.Projects')}</p>
          </div>
          <div style={{ flex: 1, background: '#fff', borderRadius: 16, padding: '14px 10px', textAlign: 'center', boxShadow: '0 4px 20px rgba(245,127,23,0.13)', border: '1.5px solid rgba(245,127,23,0.1)' }}>
            <div style={{ background: '#fdf3e8', borderRadius: 10, width: 34, height: 34, margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IonIcon icon={cubeOutline} style={{ color: '#f57f17', fontSize: 18 }} />
            </div>
            <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: '#333' }}>{complianceCards.asset_compliance_documents}</h2>
            <p style={{ margin: '3px 0 0', fontSize: 9, fontWeight: 700, color: '#6b7b8f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('Documents.Assets')}</p>
          </div>
          <div style={{ flex: 1, background: '#fff', borderRadius: 16, padding: '14px 10px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,166,90,0.13)', border: '1.5px solid rgba(0,166,90,0.1)' }}>
            <div style={{ background: '#e8f7f0', borderRadius: 10, width: 34, height: 34, margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IonIcon icon={peopleOutline} style={{ color: '#00a65a', fontSize: 18 }} />
            </div>
            <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: '#333' }}>{complianceCards.user_compliance_documents}</h2>
            <p style={{ margin: '3px 0 0', fontSize: 9, fontWeight: 700, color: '#6b7b8f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('Documents.Users')}</p>
          </div>
        </div>

        {/* ── Search + Filter ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 12px 4px' }}>
          <IonSearchbar
            value={searchText}
            placeholder={t('Documents.searchPlaceholder')}
            onIonChange={(e) => handleSearch(e.detail.value || '')}
            style={{ flex: 1, '--border-radius': '14px', '--box-shadow': '0 2px 8px rgba(0,0,0,0.06)', '--background': '#fff', padding: 0 } as any}
          />
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowFilterModal(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: '50%',
                border: activeFilterCount > 0 ? '2px solid #1d508d' : '2px solid #e0e0e0',
                background: activeFilterCount > 0 ? '#eef3fb' : '#fff',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <IonIcon icon={filterOutline} style={{ fontSize: '1.25rem', color: activeFilterCount > 0 ? '#1d508d' : '#6b7b8f' }} />
            </button>
            {activeFilterCount > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                background: '#1d508d', color: '#fff', borderRadius: '50%',
                width: 18, height: 18, fontSize: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
              }}>
                {activeFilterCount}
              </span>
            )}
          </div>
        </div>

        {/* ── Filter Modal ── */}
        <IonModal isOpen={showFilterModal} onDidDismiss={() => setShowFilterModal(false)} breakpoints={[0, 0.92]} initialBreakpoint={0.92}>
          <IonHeader>
            <IonToolbar>
              <IonTitle style={{ fontSize: '16px', fontWeight: 600 }}>{t('Documents.Filters')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowFilterModal(false)}>
                  <IonIcon icon={closeOutline} slot="icon-only" />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ '--padding-start': '16px', '--padding-end': '16px', '--padding-top': '8px', '--padding-bottom': 'calc(16px + env(safe-area-inset-bottom, 20px))' }}>
            <div style={{ paddingBottom: '24px' }}>
              <p style={sectionLabelStyle}>{t('Documents.Status_title')}</p>
              <IonItem lines="none" style={{ '--padding-start': '0', '--inner-padding-end': '0' }}>
                <IonLabel style={{ fontSize: '13px', color: '#333' }}>{t('Documents.Status_title')}</IonLabel>
                <IonSelect value={statusFilter} onIonChange={(e) => setStatusFilter(e.detail.value)}>
                  <IonSelectOption value="All">{t('Documents.Status.All')}</IonSelectOption>
                  <IonSelectOption value="Compliant">{t('Documents.Status.Compliant')}</IonSelectOption>
                  <IonSelectOption value="In Progress">{t('Documents.Status.In_Progress')}</IonSelectOption>
                  <IonSelectOption value="Completed">{t('Documents.Status.Completed')}</IonSelectOption>
                  <IonSelectOption value="Non Compliant">{t('Documents.Status.Non_Compliant')}</IonSelectOption>
                  <IonSelectOption value="Need Renewal">{t('Documents.Status.Need_Renewal')}</IonSelectOption>
                  <IonSelectOption value="Need Inspection">{t('Documents.Status.Need_Inspection')}</IonSelectOption>
                </IonSelect>
              </IonItem>

              <p style={sectionLabelStyle}>{t('Documents.Filter_by_linked_entities')}</p>

              <div className="input-container" style={{ position: 'relative' }}>
                <label className="label">{t('Documents.Project')}</label>
                {filterProject && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={chipStyle}>{filterProject}<span style={chipRemoveStyle} onClick={clearProject}>×</span></span>
                  </div>
                )}
                <input className="basic-input" placeholder={t('Documents.Search_project')} value={projectSearch} readOnly style={{ cursor: 'pointer' }}
                  onFocus={() => { if (projectOptions.length === 0) fetchProjects(''); setShowProjectDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowProjectDropdown(false), 200)}
                />
                {showProjectDropdown && (
                  <div style={dropdownStyle}>
                    {projectOptions.map((opt, i) => <div key={i} style={dropdownItemStyle} onClick={() => selectProject(opt)}>{opt.project_name || opt.name}</div>)}
                    {loadingProject && <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '12px' }}>{t('Documents.Loading')}</div>}
                  </div>
                )}
              </div>

              <div className="input-container" style={{ position: 'relative' }}>
                <label className="label">{t('Documents.Assets')}</label>
                {filterAssets.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '6px' }}>
                    {filterAssets.map((a) => <span key={a} style={chipStyle}>{a}<span style={chipRemoveStyle} onClick={() => removeAsset(a)}>×</span></span>)}
                  </div>
                )}
                <input className="basic-input" placeholder={t('Documents.Search_add_assets')} value={assetSearch}
                  onChange={(e) => { setAssetSearch(e.target.value); fetchAssets(e.target.value); setShowAssetDropdown(true); }}
                  onFocus={() => { if (assetOptions.length === 0) fetchAssets(''); setShowAssetDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowAssetDropdown(false), 200)}
                />
                {showAssetDropdown && (
                  <div style={dropdownStyle}>
                    {assetOptions.map((opt, i) => <div key={i} style={dropdownItemStyle} onClick={() => selectAsset(opt)}>{opt.name}</div>)}
                    {loadingAsset && <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '12px' }}>{t('Documents.Loading')}</div>}
                  </div>
                )}
              </div>

              <div className="input-container" style={{ position: 'relative' }}>
                <label className="label">{t('Documents.Users')}</label>
                {filterUsers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '6px' }}>
                    {filterUsers.map((u) => <span key={u} style={chipStyle}>{u}<span style={chipRemoveStyle} onClick={() => removeUser(u)}>×</span></span>)}
                  </div>
                )}
                <input className="basic-input" placeholder={t('Documents.Search_add_users')} value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); fetchUsers(e.target.value); setShowUserDropdown(true); }}
                  onFocus={() => { if (userOptions.length === 0) fetchUsers(''); setShowUserDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                />
                {showUserDropdown && (
                  <div style={dropdownStyle}>
                    {userOptions.map((opt, i) => <div key={i} style={dropdownItemStyle} onClick={() => selectUser(opt)}>{opt.full_name ? `${opt.full_name} (${opt.name})` : opt.name}</div>)}
                    {loadingUser && <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '12px' }}>{t('Documents.Loading')}</div>}
                  </div>
                )}
              </div>

              <p style={sectionLabelStyle}>{t('Documents.Sort_by')}</p>
              <div style={pillGroupStyle}>
                <span style={pillStyle(sortField === 'expiry_date')} onClick={() => setSortField((v) => v === 'expiry_date' ? '' : 'expiry_date')}>{t('Documents.expiryDate')}</span>
                <span style={pillStyle(sortField === 'creation')} onClick={() => setSortField((v) => v === 'creation' ? '' : 'creation')}>{t('Documents.Created_At')}</span>
                {sortField !== '' && (
                  <span style={{ ...pillStyle(true), background: '#e8f0fc', color: '#1d508d', border: '1.5px solid #1d508d' }} onClick={() => setSortDir((v) => v === 'asc' ? 'desc' : 'asc')}>
                    {sortDir === 'asc' ? `↑ ${t('Documents.Asc')}` : `↓ ${t('Documents.Desc')}`}
                  </span>
                )}
              </div>

              <p style={sectionLabelStyle}>{t('Documents.Documents_with_links')}</p>
              <div style={pillGroupStyle}>
                <span style={pillStyle(hasProject)} onClick={() => setHasProject((v) => !v)}>{t('Documents.Has_Project')}</span>
                <span style={pillStyle(hasAssets)} onClick={() => setHasAssets((v) => !v)}>{t('Documents.Has_Assets')}</span>
                <span style={pillStyle(hasUsers)} onClick={() => setHasUsers((v) => !v)}>{t('Documents.Has_Users')}</span>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '32px' }}>
                <button className="submit-button" style={{ flex: 1, marginTop: 0 }} onClick={handleApplyFilter}>{t('Documents.Apply_Filters')}</button>
                <button className="submit-button" style={{ flex: 1, marginTop: 0, background: '#6b7b8f' }} onClick={handleClearFilter}>{t('Documents.Clear_All')}</button>
              </div>
            </div>
          </IonContent>
        </IonModal>

        {/* ── Skeleton loaders ── */}
        {isLoading && documents.length === 0 && [1, 2, 3].map((key) => (
          <div key={key} style={{ margin: '8px 12px', background: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <IonSkeletonText animated style={{ width: '55%', height: 16, borderRadius: 8 }} />
              <IonSkeletonText animated style={{ width: '20%', height: 16, borderRadius: 8 }} />
            </div>
            <IonSkeletonText animated style={{ width: '35%', height: 12, borderRadius: 8 }} />
            <IonSkeletonText animated style={{ width: '50%', height: 12, marginTop: 6, borderRadius: 8 }} />
          </div>
        ))}

        {/* ── Document list ── */}
        <div style={{ padding: '8px 12px' }}>
          {documents.map((item: any, key: number) => (
            <Link
              key={`${item.name}-${key}`}
              to={{ pathname: '/documents/view', state: item.name }}
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                background: '#fff',
                borderRadius: 18,
                padding: '14px 16px',
                marginBottom: 10,
                boxShadow: '0 3px 14px rgba(0,0,0,0.07)',
                border: '1.5px solid rgba(29,80,141,0.07)',
                cursor: 'pointer',
              }}>
                {/* Name + Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1d2d44', flex: 1 }}>{item.name}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: getStatusColor(item.status), flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: getStatusColor(item.status) }}>{item.status || '-'}</span>
                  </div>
                </div>

                {/* Doc number & type */}
                {(item.document_number || item.document_type) && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                    {item.document_number && <span style={{ fontSize: 12, color: '#6b7b8f' }}>#{item.document_number}</span>}
                    {item.document_number && item.document_type && <span style={{ fontSize: 12, color: '#ccd4de' }}>·</span>}
                    {item.document_type && <span style={{ fontSize: 12, color: '#6b7b8f' }}>{item.document_type}</span>}
                  </div>
                )}

                {/* Dates */}
                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#888' }}>📅 {t('Documents.issuedDate')}: <b style={{ color: '#444' }}>{item.issued_date || '-'}</b></span>
                  <span style={{ fontSize: 11, color: '#888' }}>⏱ {t('Documents.expiryDate')}: <b style={{ color: '#444' }}>{item.expiry_date || '-'}</b></span>
                </div>

                {/* Expiry chip */}
                {(() => {
                  const chip = getExpiryChip(item.expiry_date);
                  if (!chip) return null;
                  return (
                    <span style={{ display: 'inline-block', marginTop: 8, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: chip.bg, color: chip.color, border: `1px solid ${chip.color}44` }}>
                      {chip.label}
                    </span>
                  );
                })()}

                {/* Attachment */}
                {item.attachment && (
                  <div style={{ marginTop: 8 }} onClick={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const base = localStorage.getItem('baseUrl')?.replace('/api', '') || '';
                    window.open(`${base}${item.attachment}`, '_system');
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#e8f0fc', color: '#1d508d', border: '1px solid #c5d8f8', cursor: 'pointer' }}>
                      <IonIcon icon={attachOutline} style={{ fontSize: 13 }} />
                      {t('Documents.View_Attachment')}
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ))}

          <IonInfiniteScroll disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => loadNextPage(e)}>
            <IonInfiniteScrollContent loadingSpinner="bubbles" loadingText={t('Documents.Loading_more_data')} />
          </IonInfiniteScroll>

          {!isLoading && documents.length === 0 && (
            <IonRow style={{ margin: 'auto', display: 'block' }}>
              <NoRecords width="60%" smallText={false} />
            </IonRow>
          )}
        </div>

        {/* ── FAB ── */}
        {canCreateDocuments && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed" style={{ marginBottom: '20px', marginRight: '10px' }}>
            <IonFabButton routerLink="/documents/create" style={{ '--background': '#1546A0' }}>
              <IonIcon icon={addOutline} style={{ fontSize: '30px' }} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
    </IonPage>
  );
};

export default DocumentsHome;

